import type { IDealerPanelContent } from '@/components/sections/dealer/panel/content';
import { DealerPanelContentGroup } from '@/components/sections/dealer/panel/content/group';
import { DealerPanelContentGroupDataRowLabelValue } from '@/components/sections/dealer/panel/content/group/data-row-label-value';
import { DealerPanelContentLabelWithIcon } from '@/components/sections/dealer/panel/content/label-with-icon';
import { DealerPanelContentOpenHour } from '@/components/sections/dealer/panel/content/open-hour';
import { ButtonIcon } from '@/components/shared/buttons/button-icon';
import { Link } from '@/components/shared/element/link';
import { Text } from '@/components/shared/element/text';
import { Flex } from '@/components/shared/layout/flex';
import { formatTel } from '@/components/shared/utility/format';
import type { IDealerSale } from '@/types/dealer/dealer';

interface DealerPanelContentGroupShowroomProps {
  dealerSale: IDealerSale;
  panel: IDealerPanelContent;
}

export const DealerPanelContentGroupShowroom: React.FunctionComponent<
  DealerPanelContentGroupShowroomProps
> = (props: DealerPanelContentGroupShowroomProps) => {
  const { dealerSale, panel } = props;
  const showroomContent = panel.showroom;
  return (
    <DealerPanelContentGroup headline={showroomContent.label}>
      <DealerPanelContentGroupDataRowLabelValue
        label={showroomContent.sell.label}
        labelIcon={showroomContent.sell.icon}
      >
        {!!dealerSale.show_room_lcv && (
          <DealerPanelContentLabelWithIcon
            icon="pickup"
            label="รถปิกอัพและรถอเนกประสงค์"
          />
        )}
        {!!dealerSale.show_room_cv && (
          <DealerPanelContentLabelWithIcon icon="truck" label="รถบรรทุก" />
        )}
      </DealerPanelContentGroupDataRowLabelValue>
      <DealerPanelContentGroupDataRowLabelValue
        label={showroomContent.businessHours.label}
        labelIcon={showroomContent.businessHours.icon}
      >
        <DealerPanelContentOpenHour
          hourMonFri={dealerSale.showroom_hour_mon_fri ?? ''}
          hourSat={dealerSale.showroom_hour_sat ?? ''}
          hourSun={dealerSale.showroom_hour_sun ?? ''}
        />
      </DealerPanelContentGroupDataRowLabelValue>
      {!!dealerSale.showroom_tel && dealerSale.showroom_tel.length > 0 && (
        <DealerPanelContentGroupDataRowLabelValue
          label={showroomContent.telephone.label}
          labelIcon={showroomContent.telephone.icon}
          valueRowGap={2}
        >
          {dealerSale.showroom_tel.map((telephone, i) => (
            <Flex key={i} justifyContent="between" columnGap="6">
              <Text as="p">
                {formatTel(telephone.tel ?? '')}{' '}
                {!!telephone.ext && `ต่อ ${telephone.ext}`}
              </Text>
              <ButtonIcon
                href={`tel:${formatTel(telephone.tel ?? '', true)}`}
                tracking={{
                  dataTrack: 'contact-dealer',
                  dataTrackText: `tel`,
                  dataTrackUrl: `tel:${formatTel(telephone.tel ?? '', true)}`,
                  dataTrackValue: `sales_${telephone.tel}`,
                }}
                test={{
                  dataTest: 'contact-dealer',
                }}
                icon="local-phone"
                variant="button-icon-color-gray"
                size="xs"
                border
              />
            </Flex>
          ))}
        </DealerPanelContentGroupDataRowLabelValue>
      )}
      {!!(dealerSale.fb_sale_cv || dealerSale.fb_sale_lcv) && (
        <DealerPanelContentGroupDataRowLabelValue
          label={showroomContent.messenger.label}
          labelIcon={showroomContent.messenger.icon}
        >
          {!!dealerSale.fb_sale_lcv && (
            <Link
              href={`https://m.me/${dealerSale.fb_sale_lcv}?ref=website_dealer`}
              test={{
                dataTest: 'contact-dealer',
              }}
              tracking={{
                dataTrack: 'contact-dealer',
                dataTrackText: `lcv_sale_facebook_messenger`,
                dataTrackUrl: `https://m.me/${dealerSale.fb_sale_lcv}?ref=website_dealer`,
                dataTrackValue: dealerSale.fb_sale_lcv,
              }}
            >
              <DealerPanelContentLabelWithIcon
                icon="pickup"
                label={dealerSale.fb_sale_lcv}
                isLink
              />
            </Link>
          )}
          {!!dealerSale.fb_sale_cv && (
            <Link
              href={`https://m.me/${dealerSale.fb_sale_cv}?ref=website_dealer`}
              test={{
                dataTest: 'contact-dealer',
              }}
              tracking={{
                dataTrack: 'contact-dealer',
                dataTrackText: `lcv_sale_facebook_messenger`,
                dataTrackUrl: `https://m.me/${dealerSale.fb_sale_cv}?ref=website_dealer`,
                dataTrackValue: dealerSale.fb_sale_cv,
              }}
            >
              <DealerPanelContentLabelWithIcon
                icon="truck"
                label={dealerSale.fb_sale_cv}
                isLink
              />
            </Link>
          )}
        </DealerPanelContentGroupDataRowLabelValue>
      )}
      {!!(dealerSale.line_sale_cv || dealerSale.line_sale_lcv) && (
        <DealerPanelContentGroupDataRowLabelValue
          label={showroomContent.line.label}
          labelIcon={showroomContent.line.icon}
        >
          {!!dealerSale.line_sale_lcv && (
            <Link
              href={`https://line.me/R/ti/p/${dealerSale.line_sale_lcv}`}
              test={{
                dataTest: 'contact-dealer',
              }}
              tracking={{
                dataTrack: 'contact-dealer',
                dataTrackText: `lcv_sale_line`,
                dataTrackUrl: `https://line.me/R/ti/p/${dealerSale.line_sale_lcv}`,
                dataTrackValue: dealerSale.line_sale_lcv,
              }}
            >
              <DealerPanelContentLabelWithIcon
                icon="pickup"
                label={dealerSale.line_sale_lcv}
                isLink
              />
            </Link>
          )}
          {!!dealerSale.line_sale_cv && (
            <Link
              href={`https://line.me/R/ti/p/${dealerSale.line_sale_cv}`}
              test={{
                dataTest: 'contact-dealer',
              }}
              tracking={{
                dataTrack: 'contact-dealer',
                dataTrackText: `lcv_sale_line`,
                dataTrackUrl: `https://line.me/R/ti/p/${dealerSale.line_sale_cv}`,
                dataTrackValue: dealerSale.line_sale_cv,
              }}
            >
              <DealerPanelContentLabelWithIcon
                icon="truck"
                label={dealerSale.line_sale_cv}
                isLink
              />
            </Link>
          )}
        </DealerPanelContentGroupDataRowLabelValue>
      )}
    </DealerPanelContentGroup>
  );
};

DealerPanelContentGroupShowroom.displayName =
  'SectionDealerPanelContentGroupShowroom';
